import React from 'react';
import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import { Heading, Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import CancelButton from '~/components/core/Buttons/CancelButton';
import { WarningIcon } from '~/components/icons';
import TextFieldFormik from '~/components/TextFieldFormik';

interface AdditionalReserveVerificationDialogProps {
  preSubmitCheckTitle: string;
  onClose: () => void;
  onProceed: (values: FormValues) => Promise<void>;
  disabled: boolean;
}

interface FormValues {
  is_override_limit: boolean;
  override_limit_reason: string;
}

const AdditionalReserveVerificationDialog: React.FC<AdditionalReserveVerificationDialogProps> = ({
  preSubmitCheckTitle,
  onClose,
  onProceed,
  disabled,
}) => {
  return (
    <Formik<FormValues>
      initialValues={{
        is_override_limit: false,
        override_limit_reason: '',
      }}
      validationSchema={Yup.object().shape({
        is_override_limit: Yup.boolean(),
        override_limit_reason: Yup.string().when('is_override_limit', {
          is: true,
          then: Yup.string().trim().required('Required'),
          otherwise: Yup.string().nullable(),
        }),
      })}
      onSubmit={async (values, { setSubmitting }: FormikHelpers<FormValues>) => {
        try {
          await onProceed(values);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {(formikProps) => {
        return (
          <CardDialog noCardTitle isDialog onClose={onClose} maxWidth="sm" fullWidth>
            <div className="text-center">
              <div className="text-yellow-700">
                <WarningIcon iconColor="currentColor" size={50} />
              </div>
              <Heading variant="h1">Please review potential issue</Heading>
              <Text className="m-12" variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                Please review the details and add a reason before proceeding.
              </Text>
            </div>
            <div className="max-h-[450px] overflow-auto">
              <div className="rounded-lg border border-slate-600 px-24 py-12">
                <div className="p-0 py-12">
                  <div>{preSubmitCheckTitle}</div>
                </div>

                <Text variant={Text.VARIANTS.SM}>Set reserve over policy limit?</Text>
                <div className="p-0 py-12">
                  <CheckboxFormik id="is_override_limit" label="Set reserve over policy limit" />
                  <TextFieldFormik
                    className="my-10 mx-0"
                    id="override_limit_reason"
                    label="Limit override Reason"
                    fullWidth
                    disabled={!formikProps?.values?.is_override_limit}
                  />
                </div>
              </div>
            </div>

            <ButtonsContainer className="mt-4 flex w-full justify-end">
              <CancelButton disabled={formikProps.isSubmitting || disabled} onClick={onClose} />
              <Button
                variant="contained"
                color="primary"
                disabled={formikProps.isSubmitting || disabled}
                onClick={(event) => {
                  event.preventDefault();
                  formikProps.handleSubmit();
                }}
              >
                Approve
              </Button>
            </ButtonsContainer>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

export default AdditionalReserveVerificationDialog;
